"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { RefreshCw, ZoomIn } from "lucide-react"
import { useBillableActions } from "@/app/context/BillableActionsContext"
import { ImageModal } from "@/app/components/ui/ImageModal"
import { GenerationLoadingComponent } from "@/app/components/GenerationLoadingComponent"
import { BillableActionInfo } from "@/app/components/BillableActionInfo"

interface CanvasConfirmationProps {
  canvasImages: string[]
  selectedCanvas: string | null
  onSelect: (canvasId: string) => void
  onRegenerate: (feedback: string) => void
  isRegenerating?: boolean
  sessionId?: string
  wizardId?: string
  userId?: string
  userName?: string
  userEmail?: string
  landscape?: boolean | null // Added landscape prop
  estimatedDuration?: number // Added estimatedDuration prop
}

export function CanvasConfirmation({
  canvasImages,
  selectedCanvas,
  onSelect,
  onRegenerate,
  isRegenerating = false,
  sessionId,
  wizardId,
  userId,
  userName,
  userEmail,
  landscape, // Added to destructuring
  estimatedDuration, // Added to destructuring
}: CanvasConfirmationProps) {
  const [showFeedback, setShowFeedback] = useState(false)
  const [feedback, setFeedback] = useState("")
  const [selectedImageForModal, setSelectedImageForModal] = useState<string | null>(null)
  const { getActionCost } = useBillableActions()

  const handleImageClick = (imageUrl: string, canvasName: string) => {
    setSelectedImageForModal(imageUrl)
  }

  const closeImageModal = () => {
    setSelectedImageForModal(null)
  }

  const handleRegenerate = async () => {
    if (feedback.trim()) {
      // Send feedback email to support if user provided feedback
      try {
        const message = `Canvas Generation Feedback

Session ID: ${sessionId || "N/A"}
Wizard ID: ${wizardId || "N/A"}
User ID: ${userId || "N/A"}

User Feedback:
${feedback.trim()}

---
Full Details (JSON):
${JSON.stringify(
  {
    sessionId: sessionId || "N/A",
    wizardId: wizardId || "N/A",
    userid: userId || "N/A",
    feedback: feedback.trim(),
  },
  null,
  2,
)}`

        await fetch("/api/support/contact", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name: userName || "Unknown User",
            email: userEmail || "no-email@roomvia.com",
            subject: "Canvas Generation Feedback",
            category: "feedback",
            message: message,
          }),
        })

        console.log("✅ Canvas feedback email sent to support")
      } catch (error) {
        console.error("❌ Failed to send feedback email:", error)
        // Don't block regeneration if email fails
      }

      // Proceed with regeneration
      onRegenerate(feedback)
      setFeedback("")
      setShowFeedback(false)
    }
  }

  const canvasRegenerationCost =
    landscape === true ? getActionCost("GENERATE_CANVASES_LANDSCAPE") : getActionCost("GENERATE_CANVASES")

  if (isRegenerating) {
    return (
      <GenerationLoadingComponent
        message="Regenerating canvas options..."
        showText={true}
        estimatedDuration={estimatedDuration} // Use prop instead of env var directly
      />
    )
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold mb-2">Select Your Canvas</h2>
        <p className="text-gray-600">Choose the canvas option that works best for your room design</p>
      </div>

      <RadioGroup value={selectedCanvas || ""} onValueChange={onSelect}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {canvasImages.map((canvasUrl, index) => {
            const canvasId = `canvas-${index}`
            const canvasName = `Canvas Option ${index + 1}`

            return (
              <div key={canvasId} className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                <div className="relative group">
                  <div
                    className="aspect-video bg-gray-100 cursor-pointer relative"
                    onClick={() => handleImageClick(canvasUrl, canvasName)}
                  >
                    <img
                      src={canvasUrl || "/placeholder.svg"}
                      alt={canvasName}
                      className="w-full h-full object-cover transition-transform group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <div className="bg-white/90 rounded-full p-2">
                        <ZoomIn className="h-6 w-6 text-gray-700" />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold">{canvasName}</h3>
                      <p className="text-sm text-gray-600">
                        {index === 0 ? "Empty room layout" : "Room with basic elements"}
                      </p>
                    </div>
                    <RadioGroupItem value={canvasId} />
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </RadioGroup>

      <ImageModal
        isOpen={!!selectedImageForModal}
        imageSrc={selectedImageForModal || ""}
        imageAlt="Canvas preview"
        caption="Canvas Preview"
        onClose={closeImageModal}
        showDownload={false}
      />

      <div className="text-center">
        <p className="text-sm text-gray-600 mb-4">Not satisfied with these options?</p>
        {!showFeedback ? (
          <>
            <Button variant="outline" onClick={() => setShowFeedback(true)} className="flex items-center gap-2">
              <RefreshCw className="h-4 w-4" />
              Regenerate Canvas Options {canvasRegenerationCost > 0 && `(${canvasRegenerationCost} credits)`}
            </Button>
            <BillableActionInfo align="left" />
          </>
        ) : (
          <div className="max-w-md mx-auto space-y-4">
            <Textarea
              placeholder="Tell us what you'd like to see different in the canvas options..."
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              className="min-h-[100px]"
            />
            <div className="flex gap-2 justify-center">
              <Button variant="outline" onClick={() => setShowFeedback(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleRegenerate}
                disabled={!feedback.trim()}
                className="bg-[#40A1AB] hover:bg-[#3A919A] text-white"
              >
                Regenerate {canvasRegenerationCost > 0 && `(${canvasRegenerationCost} credits)`}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
